// The self-service profile editor (see `features/athlete-access.md`): the only
// way an athlete or guardian changes a profile. It offers exactly the two
// self-editable fields — photos and bio — and nothing else, mirroring what the
// server will accept: identity fields (name, gender, date of birth) are
// club-managed, because they drive competition categories and the derived age
// that decides guardianship itself.
//
// Deliberately a separate, narrower dialog from the club's `AthleteDialog` rather
// than the same one with fields hidden: a form that cannot render a field cannot
// submit it by accident.

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import type { AthleteProfile } from '../../domain';
import { ATHLETE_PHOTO_MAX, COACH_DESCRIPTION_MAX } from '../../domain';
import { updateMyAthleteProfile, uploadAthletePhoto } from '../../data/apiClient';
import { useTranslation } from '../../state/I18nContext';

interface MyProfileEditDialogProps {
  open: boolean;
  profile: AthleteProfile;
  onClose: () => void;
  onSaved: (profile: AthleteProfile) => void;
}

export function MyProfileEditDialog({
  open,
  profile,
  onClose,
  onSaved,
}: MyProfileEditDialogProps) {
  const { t } = useTranslation();
  const [photoUrls, setPhotoUrls] = useState<string[]>(profile.photoUrls);
  const [bio, setBio] = useState(profile.coachDescription);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Re-seed whenever the dialog opens, so a cancelled edit never leaks into the
  // next one.
  useEffect(() => {
    if (open) {
      setPhotoUrls(profile.photoUrls);
      setBio(profile.coachDescription);
      setError(null);
    }
  }, [open, profile]);

  const pickPhoto = () => fileRef.current?.click();

  const onFile = async (file: File | undefined) => {
    if (!file || photoUrls.length >= ATHLETE_PHOTO_MAX) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadAthletePhoto(file);
      setPhotoUrls((current) => [...current, url]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athlete.profile.edit.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await updateMyAthleteProfile(profile.personalNumber, {
          photoUrls,
          coachDescription: bio,
        }),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athlete.profile.edit.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('athlete.profile.edit.title')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('athlete.profile.edit.clubManagedHint')}
        </Typography>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('athlete.profile.edit.bio')}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            helperText={t('athlete.profile.edit.bioHelp', { max: COACH_DESCRIPTION_MAX })}
            slotProps={{ htmlInput: { maxLength: COACH_DESCRIPTION_MAX } }}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('athlete.profile.edit.photos', {
                count: photoUrls.length,
                max: ATHLETE_PHOTO_MAX,
              })}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {photoUrls.map((url) => (
                <Box
                  key={url}
                  sx={{
                    position: 'relative',
                    width: 96,
                    height: 96,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={profile.fullName}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    aria-label={t('athlete.profile.edit.removePhoto')}
                    disabled={busy}
                    onClick={() =>
                      setPhotoUrls((current) => current.filter((u) => u !== url))
                    }
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.55)',
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {photoUrls.length < ATHLETE_PHOTO_MAX && (
                <Button
                  variant="outlined"
                  disabled={busy || uploading}
                  onClick={pickPhoto}
                  sx={{ width: 96, height: 96, flexDirection: 'column', gap: 0.5 }}
                >
                  {uploading ? <CircularProgress size={24} /> : <AddPhotoAlternateIcon />}
                  <Typography variant="caption">
                    {t('athlete.profile.edit.addPhoto')}
                  </Typography>
                </Button>
              )}
            </Stack>
            {/* MUI has no file-picker component; the input is visually hidden and
                driven by the button above (same pattern as AthleteDialog). */}
            <Box
              component="input"
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                void onFile(e.target.files?.[0])
              }
              sx={{ display: 'none' }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('athlete.profile.edit.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy || uploading}>
          {t('athlete.profile.edit.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
